/**
 * Who is signed in, and the token that says so.
 *
 * The seam is deliberately the same shape as `core-api`'s `TokenVerifier`: two
 * implementations, one interface, and nothing downstream knows which it has.
 * There it is "verify a signature or do not"; here it is "ask Firebase for a
 * token or mint the unsigned dev shape". Both ends of the wire therefore have
 * one place each where development differs from production, and the rest of both
 * programs is identical either way.
 */

export interface StaffUser {
  uid: string;
  email: string | null;
}

/**
 * What a support session knows about itself without asking the server — read
 * off the token the console handed over (`auth/support.ts`). `ended` is the
 * one fact the provider decides: past `expiresAt`, or after "End support
 * view", the token is no longer presented and the shell shows the ended
 * screen rather than a sign-in form the seat could not use.
 */
export interface SupportSessionState {
  tenantId: string;
  operator: string;
  expiresAt: string;
  ended: boolean;
}

export interface AuthProvider {
  /**
   * `support` is the third implementation (`auth/support.ts`): the operator
   * console's fifteen-minute read-only look, which is a fixed credential with
   * no refresh and no sign-in. Everything downstream still sees one interface.
   */
  readonly kind: 'firebase' | 'fake' | 'support';
  /**
   * Present on the support provider alone. `undefined` — not a method that
   * answers `null` — on the other two, so the one place that reads it
   * (`runtime.supportSession()`) says which kind of seat this is in one
   * expression and every real seat pays nothing.
   */
  readonly support?: () => SupportSessionState;
  /** Null until the first `onChange`, then the signed-in user or null. */
  current(): StaffUser | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /**
   * The ID token, refreshed when asked. Returns null when nobody is signed in —
   * the `ApiClient` sends the request anyway and lets `core-api`'s `401` be the
   * authority on it, rather than this app inventing a second opinion about
   * whether a credential is good.
   */
  token(forceRefresh?: boolean): Promise<string | null>;
  onChange(listener: (user: StaffUser | null) => void): () => void;
}

export class AuthError extends Error {
  readonly reason: 'bad_credentials' | 'unavailable';
  constructor(reason: 'bad_credentials' | 'unavailable', message: string) {
    super(message);
    this.name = 'AuthError';
    this.reason = reason;
  }
}
